import type { SensorState } from "@/base/sensor-events"
import type { DeviceSortKey, SortDirectionKey } from "@/base/Sorting"
import type { AcceptsChildren } from "@/theme"

import * as Geo from "@/base/geo"
import { logger, makeFileLogger } from "@/base/logger"
import { isNumberValid, isTruthyString } from "@/base/type-guards"
import * as Models from "@/redux/models"
import * as turf from "@turf/turf"
import { createCachedSelector } from "re-reselect"
import { createSelector } from "reselect"

import {
  type DeviceConfiguration,
  type DeviceSummary,
} from "./device-configurations.reducer"
import type { RootState } from "./root.reducer"

import { useRootSelector } from "./useRootSelector"
import { canSeedTestDatabase, ENVIRONMENT } from "@/base/environment"

const fileLogger = makeFileLogger({ fileName: "selectors.ts" })

export interface LocationValidationArgs {
  farmLocation: Geo.PointInput | null | undefined
  gpsStateCurrent?: SensorState<"gps"> | null
}

/**
 * Use this to filter out locations that are super far away from the farm.
 * This happens sometimes due to problems with our gps
 */
export function isValidGpsLocation<T extends Geo.AnyPoint>(
  targetLocation: T | null | undefined,
  { farmLocation, gpsStateCurrent }: LocationValidationArgs,
): targetLocation is T {
  const farmCoords = Geo.point(farmLocation)?.getCoords()
  const locationCoords = Geo.point(targetLocation)?.getCoords()

  let isValidGpsState = true
  let farmDistanceMiles = 0

  if (typeof gpsStateCurrent === "string") {
    isValidGpsState = gpsStateCurrent === "GLK" || gpsStateCurrent === "GST"
  }
  if (isValidGpsState && locationCoords && farmCoords) {
    if (targetLocation) {
      try {
        farmDistanceMiles = turf.distance(
          turf.point(farmCoords),
          turf.point(locationCoords),
          { units: "miles" },
        )
      } catch (error) {
        fileLogger.error(error)
        return false
      }
      return farmDistanceMiles < 300
    }
  }

  return false
}

export function makeLocationValidator({
  farmLocation,
}: {
  farmLocation: Geo.AnyPoint | null | undefined
}) {
  return <T extends Geo.AnyPoint>(
    location: T | null | undefined,
    gpsStateCurrent?: SensorState<"gps"> | null,
  ): location is T => {
    const asGeoJson = Geo.point(farmLocation)?.toJson()
    if (asGeoJson) {
      return isValidGpsLocation(location, { farmLocation, gpsStateCurrent })
    }
    return false
  }
}

export function makeDeviceSummary({
  configuration,
  event,
  farmLocation,
}: {
  configuration: DeviceConfiguration
  event: Models.DeviceEvent | null | undefined
  farmLocation: Geo.AnyPoint | null | undefined
}): DeviceSummary {
  const { firmwareVersion, gps } = event ?? {}
  const isLocationValid = makeLocationValidator({ farmLocation })

  // Location permanent overrides event location
  const locationResolved = configuration.locationPermanent ?? gps?.location
  const gpsLocation = isLocationValid(locationResolved)
    ? Geo.createMultiPoint(locationResolved)
    : undefined
  return {
    codaDeviceAlias: configuration.codaDeviceAlias,
    configurationId: configuration.id,
    deviceId: configuration.deviceId,
    deviceInstallationType: configuration.deviceInstallationType,
    deviceName: configuration.deviceName ?? configuration.codaDeviceAlias,
    firmwareVersion: firmwareVersion ?? null,
    gpsLocation,
    hardwareGeneration: configuration.hardwareGeneration,
  }
}

/**
 * Selector result will be recalculated when the device id,
 * last event id, or configuration id change
 */
export function makeDeviceEventCacheKey(
  state: RootState,
  deviceId: string | null | undefined = "None",
): string {
  if (typeof deviceId === "string") {
    const event = state.deviceEventLast.entities[deviceId]
    const configuration = state.deviceConfigurations.entities[deviceId]
    const eventId = event?.id ?? "None"
    const configId = configuration?.id ?? "None"
    return `device=${deviceId}/event=${eventId}/configuration=${configId}`
  }
  return ""
}

/**
 *
 */
export function getActiveUserEmailFromState(
  state: RootState,
): string | undefined {
  return state.auth.email
}

export function getActiveUserIdFromState(
  state: RootState,
): string | undefined {
  return state.auth.userId
}

export function getIsAccountDeactivatedFromState(
  state: RootState,
): boolean {
  return state.auth.isAccountDeactivated === true
}

export function getUserMeasurementPreferenceFromState(state: RootState) {
  return state.userPreferences.measurement
}
export function useMeasurementPreference() {
  return useRootSelector(getUserMeasurementPreferenceFromState)
}

export function getUserIsAdminFromState(state: RootState): boolean {
  return state.userPermissions.isAdmin === true
}

export function getIsAdminModeEnabledFromState(state: RootState): boolean {
  return (
    getUserIsAdminFromState(state) &&
    state.userPermissions.isAdminModeEnabled === true
  )
}

export function getMapTypeIdFromState(state: RootState): Geo.MapTypeId {
  return state.userPreferences.mapTypeId
}

export function isDevicePair(trigger: Models.EventActionTrigger): boolean {
  if (
    typeof trigger.targetDeviceId === "string" &&
    trigger.namedDeviceAction?.actionType === "RLYAS"
  ) {
    if (trigger.sourceSensor === "reel") {
      return (
        trigger.sourceSensorStateCurrent === "RS" &&
        trigger.sourceSensorStatePrevious === "RR"
      )
    } else if (trigger.sourceSensor === "wheel") {
      return (
        trigger.sourceSensorStateCurrent === "WF" &&
        trigger.sourceSensorStatePrevious === "WL"
      )
    }
  }
  return false
}
export function isCustomNotificationTrigger(
  trigger: Models.EventActionTrigger,
) {
  if (
    trigger.notify === true &&
    !Boolean(trigger.targetDeviceId) &&
    !trigger.namedDeviceAction
  ) {
    return true
  }
  return false
}

export function getIsTermsOfServiceAcceptedFromState(state: RootState) {
  return state.userPermissions.termsOfServiceAccepted === true
}

export function getDeviceSortKeyFromState(state: RootState): DeviceSortKey {
  return state.deviceConfigurations.deviceRoster.sortKey
}
export function getDeviceSortDirectionFromState(
  state: RootState,
): SortDirectionKey {
  return state.deviceConfigurations.deviceRoster.sortDirection
}

// TODO: Delete
export const getEventsNearFieldByFieldIdFromState: (
  state: RootState,
  fieldId: number,
) => string[] | undefined = createCachedSelector(
  Models.deviceEventLast.selectAll,
  Models.farmField.selectById,
  (events, field) => {
    return field
      ? events
          .filter((de): boolean => {
            const location = de.gps?.location
            if (location) {
              const buffered = turf.buffer(field.polygon, 0, {
                units: "meters",
              })
              if (buffered) {
                try {
                  return turf.booleanPointInPolygon(location, buffered)
                } catch (error) {
                  logger.error(error)
                }
              }
            }
            return false
          })
          .map((de) => de.deviceId)
      : []
  },
)((_state, fieldId) => fieldId)

export function getAreReelRunOutlinesVisibleFromState(
  state: RootState,
): boolean {
  return state.fieldIrrigationHistory.showOutlines === true
}

/**
 * Load user's active farm id from app state, which in tu∏rn reads it from
 * the browser or phone's respective localstorage implementation. An undefined
 * value here means that the storage hasn't been read yet, while null indicates
 * that it has been read and the user does not have an active farm id stored.
 * @param state
 * @returns {number | null | undefined}
 */
export function getDemoFarmIdOrActiveFarmIdFromState({
  userFarms: { activeFarmId, demoFarmId },
}: RootState) {
  return demoFarmId ?? activeFarmId
}

/**
 *
 * @param state
 */
export function getIsDemoModeActiveFromState(state: RootState) {
  const isDemoActive = getActiveFarmFromState(state)?.isDemo === true
  return isDemoActive
}

/**
 *
 * @param state
 */
export function getLastCacheCalculatedDateFromState(state: RootState) {
  return state.userFarms.lastCacheCalculatedDate
}

/**
 *
 * @param state
 */
export const getActiveFarmFromState = createSelector(
  Models.userFarm.selectEntities,
  getDemoFarmIdOrActiveFarmIdFromState,
  (farms, id) => {
    if (typeof id === "number") {
      return farms[id]
    }
    return undefined
  },
)

/**
 *
 * @param state
 */
export function getActiveFarmNameFromState(state: RootState) {
  return getActiveFarmFromState(state)?.name
}

export const getActiveFarmCoordinatesFromState = createSelector(
  getActiveFarmFromState,
  (farm): Geo.Coordinates | undefined => {
    return Geo.point(farm?.gpsLocation ?? undefined)?.getCoords()
  },
)
export const getActiveFarmLng = createSelector(
  getActiveFarmCoordinatesFromState,
  (coord) => coord?.[0],
)
export const getActiveFarmLat = createSelector(
  getActiveFarmCoordinatesFromState,
  (coord) => coord?.[1],
)
export const getActiveFarmLatLngFromState = createSelector(
  getActiveFarmCoordinatesFromState,
  (location) => Geo.createMultiPoint(location),
)

export const getActiveFarmLatLngWeb = createSelector(
  getActiveFarmLng,
  getActiveFarmLat,
  (lng, lat): google.maps.LatLngLiteral | undefined => {
    if (isNumberValid(lng) && isNumberValid(lat)) {
      return { lat, lng }
    }
    return undefined
  },
)

export interface PairLineProps extends Models.EventActionTriggerIds {
  key: string
  linePath: Geo.MultiPath | undefined
  sourceLocation: Geo.MultiPoint | undefined
  targetLocation: Geo.MultiPoint | undefined
  triggerId: number
}

export const getConnectionLinesFromState = createSelector(
  Models.deviceConnection.selectAll,
  Models.deviceEventLast.selectEntities,
  getActiveFarmCoordinatesFromState,
  (triggers, events, farmLocation): PairLineProps[] => {
    const acc = [] as PairLineProps[]
    for (const trigger of triggers) {
      const targetDeviceId = trigger.targetDeviceId
      const sourceDeviceId = trigger.sourceDeviceId
      const sourceEvent = events[sourceDeviceId]
      let values: PairLineProps | undefined
      if (isTruthyString(targetDeviceId)) {
        const targetEvent = events[targetDeviceId]
        const sourceGeoJson = sourceEvent?.gps?.location
        const targetGeoJson = targetEvent?.gps?.location
        if (
          isValidGpsLocation(sourceGeoJson, {
            farmLocation,
            gpsStateCurrent: sourceEvent?.gps?.stateCurrent,
          }) &&
          isValidGpsLocation(targetGeoJson, {
            farmLocation,
            gpsStateCurrent: targetEvent?.gps?.stateCurrent,
          })
        ) {
          const sourceLocation = Geo.createMultiPoint(sourceGeoJson)
          const targetLocation = Geo.createMultiPoint(targetGeoJson)
          if (sourceLocation && targetLocation) {
            values = {
              key: `${trigger.id}`,
              linePath: {
                native: [sourceLocation.native, targetLocation.native],
                web: [sourceLocation.web, targetLocation.web],
              },
              sourceDeviceId,
              sourceLocation,
              targetDeviceId,
              targetLocation,
              triggerId: trigger.id,
            }
          }
        }
      }
      if (values) {
        acc.push(values)
      }
    }
    return acc
  },
)

function makeDeviceSummaryCacheKey(
  state: RootState,
  deviceId: string | null | undefined,
): string {
  if (typeof deviceId === "string") {
    const configuration: DeviceConfiguration | undefined =
      Models.deviceConfiguration.selectById(state, deviceId)
    const eventId = Models.deviceEventLast.selectById(state, deviceId)?.id
    return `${deviceId}/${configuration?.id ?? ""}/${
      configuration?.deviceName ?? ""
    }/${eventId ?? ""}`
  }
  return ""
}

function getAliasFromProps(
  _state: RootState,
  props: Models.CodaDeviceAliasProps,
): string {
  return props.codaDeviceAlias
}

export const getDeviceSummaryByAliasFromState: (
  state: RootState,
  props: Models.CodaDeviceAliasProps,
) => DeviceSummary | undefined = createCachedSelector(
  Models.deviceConfiguration.selectAll,
  Models.deviceEventLast.selectEntities,
  getAliasFromProps,
  getActiveFarmCoordinatesFromState,
  (configurations, events, alias, farmLocation) => {
    const configuration = configurations.find(
      (dc) => dc.codaDeviceAlias === alias,
    )
    const event = configuration ? events[configuration.deviceId] : undefined
    if (configuration) {
      return makeDeviceSummary({ configuration, event, farmLocation })
    }
    return undefined
  },
)(getAliasFromProps)
/**
 * Get alias, deviceId, installation type and name for a device configuration if
 * it exists in state
 *
 * @param id
 */
export const getDeviceSummaryByDeviceIdFromState: (
  state: RootState,
  deviceId: string,
) => DeviceSummary | undefined = createCachedSelector(
  Models.deviceConfiguration.selectById,
  Models.deviceEventLast.selectById,
  getActiveFarmCoordinatesFromState,
  (configuration, event, farmLocation): DeviceSummary | undefined => {
    if (configuration) {
      return makeDeviceSummary({
        configuration,
        event,
        farmLocation,
      })
    }
    return undefined
  },
)(makeDeviceSummaryCacheKey)
export interface DeviceSummaryProviderOwnProps extends AcceptsChildren {
  codaDeviceAlias: string | undefined
  deviceId: string | undefined
}

export const getDeviceAnalyticsDateValue = {
  // MAX DATE
  getMax: (state: RootState) => state.deviceProfile.analytics.dateMsMax,

  // MIN DATE
  getMin: (state: RootState) => state.deviceProfile.analytics.dateMsMin,
}

export const getMissingFieldsForActiveRuns = createSelector(
  [
    Models.reelRunsActive.selectAll,
    Models.farmField.selectEntities,
    Models.deviceConfiguration.selectEntities,
    Models.deviceEventLast.selectEntities,
    getActiveFarmCoordinatesFromState,
  ],
  (runs, fields, configurations, events) =>
    runs
      .filter(
        /**
         * Only include runs with no field data associated
         */
        ({ fieldId }) => {
          // The run has no associated field - user should draw one
          if (
            fieldId === null ||
            typeof fieldId === "undefined" ||
            !fields[fieldId]
          ) {
            return true
          }
          return false
        },
      )
      .map(
        /**
         * Add device name to display in list, and event location to focus map
         */
        (runData) => {
          const deviceId = runData.deviceId ?? ""
          const { codaDeviceAlias, deviceName } =
            configurations[deviceId] ?? {}
          const { location } = events[deviceId]?.gps ?? {}

          return {
            ...runData,
            deviceName: deviceName ?? codaDeviceAlias,
            location,
          }
        },
      ),
)

export function getUserFarmErrorCodeFromState(state: RootState) {
  return state.userFarms.errorCode
}

export function getUserNameGivenFromState(state: RootState) {
  return state.auth.userNameGiven
}
export function getUserNameFamilyFromState(state: RootState) {
  return state.auth.userNameFamily
}

export function getIsUserNamedProvidedFromState(state: RootState) {
  if (
    Boolean(getUserNameGivenFromState(state)) &&
    Boolean(getUserNameFamilyFromState(state))
  ) {
    return true
  }
  return false
}

/**
 * Find the first reel run that has a device id matching the provided device id
 *
 * @param {RootState} state
 * @param {string|undefined} deviceId
 * @returns the id of the run
 */
export const getReelRunByDeviceIdCached: (
  state: RootState,
  deviceId: string,
) => Models.ReelRun | undefined = createCachedSelector(
  Models.reelRunsActive.selectAll,
  (_state: RootState, id: string) => id,
  function getReelRunByDeviceId(reelRuns, deviceId) {
    return reelRuns.find((reelRun) => {
      return reelRun.deviceId === deviceId
    })
  },
)((_state, id) => id)

/**
 * Find the first reel run that has a device id matching the provided device id
 *
 * @returns the id of the run
 */
export const getReelRunIdByDeviceId: (
  state: RootState,
  deviceId: string,
) => number | undefined = createSelector(
  getReelRunByDeviceIdCached,
  (run) => run?.reelRunId,
)

export function findFieldForDevice({
  event,
  fields,
}: {
  event: Models.DeviceEvent | null | undefined
  fields: Models.FarmField[]
}) {
  let result: Models.FarmField | undefined
  if (event) {
    const coordinates = event.gps?.location?.coordinates
    if (coordinates) {
      result = fields.find((field): boolean => {
        try {
          return turf.booleanPointInPolygon(
            turf.point(coordinates),
            turf.polygon(field.polygon.coordinates),
          )
        } catch (error) {
          logger.debug(error)
          return false
        }
      })
    }
  }
  return result ?? null
}

export function getActiveFeatureNameFromState(state: RootState) {
  return state.statusMap.activeFeature
}

export function getIsChangeFarmsOpenFromState(state: RootState) {
  return getActiveFeatureNameFromState(state) === "changeFarmAccount"
}
export function getIsSeedDatabaseOpenFromState(state: RootState) {
  return (
    getActiveFeatureNameFromState(state) === "seedDatabase" &&
    canSeedTestDatabase(ENVIRONMENT.targetDatabaseName)
  )
}

export function getDeviceConfigurationById(state: RootState, deviceId: string) {
  return Models.deviceConfiguration.selectById(state, deviceId)
}
